import * as Sentry from '@sentry/react';
import { createBrowserRouter, Navigate, RouteObject } from 'react-router-dom';
import AuthenticationGuard from '~/containers/auth/AuthenticationGuard';
import LoginPage from '~/containers/auth/LoginPage';
import LogoutPage from '~/containers/auth/LogoutPage';
import ErrorPage from '~/containers/errors/ErrorPage';
import RouteErrorBoundary from '~/containers/errors/RouteErrorBoundary';
import { WORKFLOW_STEPS } from '~/containers/viewer/explorer-sidebar/WorkflowStepper/workflow-steps';
import DemoRoute from './demo-route';
import ViewerLayout from './layout';
import ProtectedRoute from './protected-route';
import SessionLayout from './SessionLayout';
import Viewer from './viewer';

const createWorkflowRoutes = (): RouteObject[] =>
    WORKFLOW_STEPS.flatMap((step) => [
        {
            path: step.path,
            element: <Viewer />,
            errorElement: <RouteErrorBoundary />,
        },
        {
            path: `${step.path}/:patientID`,
            element: <Viewer />,
            errorElement: <RouteErrorBoundary />,
        },
    ]);

const createSessionWorkflowRoutes = (): RouteObject[] =>
    WORKFLOW_STEPS.map((step) => ({
        // Strip leading slash for relative child paths
        path: step.path.replace(/^\//, ''),
        element: <Viewer />,
        errorElement: <RouteErrorBoundary />,
    }));

const sentryCreateBrowserRouter =
    Sentry.wrapCreateBrowserRouter(createBrowserRouter);

const router = sentryCreateBrowserRouter([
    {
        path: '/login',
        element: <LoginPage />,
        errorElement: <RouteErrorBoundary />,
    },
    {
        path: '/logout',
        element: <LogoutPage />,
        errorElement: <RouteErrorBoundary />,
    },

    {
        path: '/',
        element: (
            <AuthenticationGuard>
                <ProtectedRoute>
                    <DemoRoute>
                        <ViewerLayout />
                    </DemoRoute>
                </ProtectedRoute>
            </AuthenticationGuard>
        ),
        errorElement: <RouteErrorBoundary />,
        children: [
            {
                path: '/',
                element: <Navigate to="/setup" replace />,
            },
            {
                path: '/session/:sessionId',
                element: <SessionLayout />,
                errorElement: <RouteErrorBoundary />,
                children: [
                    {
                        index: true,
                        element: <Navigate to="contouring" replace />,
                    },
                    ...createSessionWorkflowRoutes(),
                ],
            },
            ...createWorkflowRoutes(),
        ],
    },

    {
        path: '/access-denied',
        element: (
            <ErrorPage
                status={401}
                title={'You are not authorized to see this page.'}
            />
        ),
    },
]);

export default router;
